import { Fragment, ReactNode } from 'react';
import { Grid, FormHelperText } from '@mui/material';
import { Control, FieldError, FieldErrorsImpl, UseFormSetValue } from 'react-hook-form';
import { TextField } from '@/components/forms/inputs/TextField/TextField';
import { SelectField, SelectFieldData } from '@/components/forms/inputs/SelectField/SelectField';
import { DatePickerField } from '@/components/forms/inputs/DatePickerField/DatePickerField';
import { InputFieldSuggestions } from '@/components/forms/inputs/InputFieldSuggestions/InputFieldSuggestions';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import { DefaultPageProps } from '@/types/types/page/pages';
import {
    IPaymentDetailsFormField,
    IWalletStateData,
    IWalletUploadedDocument,
} from '@/types/types/forms/wallet/wallet.interface';
import { isArrayWithValues } from '@/utils/fp/array/array';
import { createSelectOptionsFromArray } from '@/utils/forms/selectOptions';
import { JSXFromString } from '@/utils/JSXFromString';
import { WalletFileUpload } from '../WalletFileUpload/WalletFileUpload';
import * as Sentry from '@sentry/nextjs';

export const WALLET_DETAILS_TEXT_AREA_FIELDS = ['notes'];
export const WALLET_DETAILS_FULLWIDTH_FIELDS = [
    'address1',
    'address2',
    'notes',
    'invoiceFile',
    'businessName',
];
export const WALLET_DETAILS_SELECT_FIELDS = [
    'countryCode',
    'specialtyId',
    'graduationYear',
    'stateCode',
];
export const WALLET_DETAILS_DATE_FIELDS = ['invoiceDate'];
export const WALLET_DETAILS_FILE_UPLOAD_FIELDS = ['invoiceFile'];

interface WalletPaymentDetailsFieldsProps<T extends object> {
    dataFields: IPaymentDetailsFormField[];
    dictionary: DefaultPageProps['dictionary'];
    states: IWalletStateData[] | undefined;
    control: Control;
    setValue: UseFormSetValue<Partial<T>>;
    documents?: Record<string, IWalletUploadedDocument[]>;
    updatePaymentDocumentsById?: (id: string, newDocuments: IWalletUploadedDocument[]) => void;
    errors?: Partial<FieldErrorsImpl<T>>;
    statesList?: string[];
    citiesList?: string[];
    prependField?: ReactNode;
    onZipChange?: () => void;
}

export const WalletPaymentDetailsFields = <T extends object>({
    dataFields,
    dictionary,
    states,
    control,
    setValue,
    documents,
    updatePaymentDocumentsById,
    errors,
    statesList,
    citiesList,
    prependField,
    onZipChange,
}: WalletPaymentDetailsFieldsProps<T>) => {
    const { t } = usePhrase();

    const { countries, specialties } = dictionary;

    const isIndiaPaymentForm = dataFields?.some((item) => item.id === 'aadhaarNumber');

    const hasMoreThanOneState = statesList && statesList?.length > 1;

    const hasEmptyColumnAfterField = (id: string) => id === 'email' && isIndiaPaymentForm;

    const isTextAreaField = (id: string) => WALLET_DETAILS_TEXT_AREA_FIELDS.includes(id);
    const isSelectField = (id: string) => WALLET_DETAILS_SELECT_FIELDS.includes(id);
    const isDateField = (id: string) => WALLET_DETAILS_DATE_FIELDS.includes(id);
    const isFileUploadField = (id: string) => WALLET_DETAILS_FILE_UPLOAD_FIELDS.includes(id);
    const isFullWidthField = (id: string) => WALLET_DETAILS_FULLWIDTH_FIELDS.includes(id);

    const checkForError = (id?: string, labelPhraseId?: number) => {
        if (!id) {
            Sentry.captureException(new Error('Wallet payment details field is missing an id'), {
                extra: {
                    labelPhraseId,
                },
            });
            return true;
        }
        return false;
    };

    const countriesOptions: SelectFieldData<string>[] = createSelectOptionsFromArray({
        label: 'phraseId',
        value: 'countryCode',
    })(countries);

    const specialtiesOptions: SelectFieldData<number>[] = createSelectOptionsFromArray({
        label: 'phraseId',
        value: 'specialtyId',
    })(specialties);

    const statesOptions: SelectFieldData<string>[] = createSelectOptionsFromArray({
        label: 'name',
        value: 'stateCode',
    })(states ?? []);

    const yearsOptions: SelectFieldData<string>[] = (() => {
        const years = Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i);
        return years.map((item) => ({
            label: item.toString(),
            value: item.toString(),
        }));
    })();

    const getSelectOptions = (id: string): SelectFieldData[] => {
        switch (id) {
            case 'countryCode':
                return countriesOptions;
            case 'specialtyId':
                return specialtiesOptions;
            case 'graduationYear':
                return yearsOptions;
            case 'stateCode':
                return statesOptions;
            default:
                return [];
        }
    };

    const isStatesFieldWithNoOptions = (id: string) =>
        id === 'stateCode' && !isArrayWithValues(statesOptions);

    const PhraseNotes = ({ notesPhraseIds }: { notesPhraseIds: number[] }) => {
        return (
            <>
                {isArrayWithValues(notesPhraseIds) && (
                    <>
                        {notesPhraseIds.map((item) => (
                            <FormHelperText key={item}>
                                {JSXFromString(t(Number(item)))}
                            </FormHelperText>
                        ))}
                    </>
                )}
            </>
        );
    };

    const renderField = ({
        required,
        maxLength,
        notesPhraseIds,
        disabled,
        labelPhraseId,
        id,
        mask,
        minLength,
        maxFileSizeMB,
        maxFileCount,
        supportedFormats,
        fileFieldPhraseId,
    }: IPaymentDetailsFormField) => {
        if (!checkForError(id, labelPhraseId)) {
            const fieldError = errors?.[id as keyof T] as FieldError;

            const commonProps = {
                control,
                name: id,
                label: labelPhraseId ?? ' ',
                disabled,
                required,
                fullWidth: true,
                error: fieldError,
            };

            const gridProps = {
                style: id === 'address2' ? { marginTop: '-24px' } : {},
            };

            const blankSpaceGridId = `${id}-blankspace`;

            const renderFieldContent = () => {
                if (isFileUploadField(id)) {
                    return (
                        <WalletFileUpload
                            id={id}
                            control={control}
                            fileFieldPhraseId={fileFieldPhraseId}
                            labelPhraseId={labelPhraseId}
                            required={required}
                            maxFileSizeMB={maxFileSizeMB}
                            maxFileCount={maxFileCount}
                            supportedFormats={supportedFormats}
                            documents={documents}
                            updatePaymentDocumentsById={updatePaymentDocumentsById}
                            error={fieldError}
                        />
                    );
                }

                if (isDateField(id)) {
                    return <DatePickerField {...commonProps} name={id} />;
                }

                if (isSelectField(id) && !isStatesFieldWithNoOptions(id)) {
                    return (
                        <>
                            <SelectField {...commonProps} data={getSelectOptions(id)} />
                            {id === 'stateCode' && hasMoreThanOneState && (
                                <InputFieldSuggestions
                                    name="stateCode"
                                    data={statesList}
                                    setValue={setValue}
                                />
                            )}
                        </>
                    );
                }

                if (isTextAreaField(id)) {
                    return (
                        <TextField
                            {...commonProps}
                            mask={mask}
                            multiline
                            minRows={3}
                            slotProps={{
                                htmlInput: {
                                    maxLength,
                                    minLength,
                                },
                            }}
                        />
                    );
                }

                return (
                    <>
                        <TextField
                            {...commonProps}
                            mask={mask}
                            slotProps={{
                                htmlInput: {
                                    maxLength,
                                    minLength,
                                },
                            }}
                            onChange={() => id === 'zip' && onZipChange?.()}
                        />
                        {id === 'city' && (
                            <InputFieldSuggestions
                                name="city"
                                data={citiesList}
                                setValue={setValue}
                            />
                        )}
                    </>
                );
            };

            return (
                <Fragment key={`{${id}-fragment}`}>
                    <Grid
                        size={{
                            xs: 12,
                            md: isFullWidthField(id) ? 12 : 6,
                        }}
                        key={id}
                        {...gridProps}
                    >
                        {renderFieldContent()}
                        <PhraseNotes notesPhraseIds={notesPhraseIds} />
                    </Grid>
                    {hasEmptyColumnAfterField(id) && (
                        <Grid
                            data-testid={blankSpaceGridId}
                            key={blankSpaceGridId}
                            display={{ xs: 'none', md: 'inline-flex' }}
                            size={{ md: 6 }}
                        />
                    )}
                </Fragment>
            );
        }

        return null;
    };

    return (
        <Grid container spacing={3}>
            {prependField}
            {dataFields && dataFields.map(renderField)}
        </Grid>
    );
};
